import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'
import axios from 'axios'
import useIsMounted from '../../hooks/useIsMounted'
import apiConfig from '../../config/api-config'
import localConfig from '../../config/local-config'
import nrLogger from '../helpers/nrLogger'

export const PopularProductsContext = createContext()
export default function PopularProductsProvider({
  children,
  products,
  popularProduct,
  attributes,
  vehicle,
  canonicalUri,
  pageType,
}) {
  const isMounted = useIsMounted()
  const isDataPresent = Boolean(popularProduct)
  const [popularProducts, setPopularProducts] = useState(() => {
    if (isDataPresent) return popularProduct
    return []
  })
  const baseUrl = apiConfig.apiURL
  const contextValue = useMemo(
    () => ({
      popularProducts,
      setPopularProducts,
    }),
    [
      popularProducts,
      setPopularProducts,
    ],
  )

  const parseAttribute = useMemo(() => ({
    'vehicle[year]': attributes?.year?.year ?? null,
    'vehicle[make]': attributes?.make?.make_name ?? null,
    'vehicle[model]': attributes?.model?.model_name ?? null,
    'vehicle[engine]': attributes?.engine?.engine_name ?? null,
    'filters[part]': attributes?.part?.part_name ?? null,
    'filters[brand]': attributes?.brand?.brand_name ?? null,
  }), [attributes])

  const parseVehicle = useMemo(() => ({
    'vehicle[year]': parseInt(vehicle?.year, 10) ?? null,
    'vehicle[make]': vehicle?.make ?? null,
    'vehicle[model]': vehicle?.model ?? null,
  }), [vehicle])

  const parseRefinement = useCallback(type => {
    const { refinements } = products
    const { available } = refinements
    const indexNumber = available.findIndex(record => record.name === type)
    if (indexNumber === -1) {
      return null
    }
    const data = available[indexNumber].values.slice(0, 6)
    const refinementData = []
    data.forEach(record => {
      refinementData.push(record.text)
    })
    if (type === 'Part') {
      return {
        'refinements[part]': JSON.stringify(refinementData),
      }
    }
    return {
      'refinements[brand]': JSON.stringify(refinementData),
    }
  }, [products])

  const otherQuery = useCallback(type => {
    const vehicleData = attributes ? parseAttribute : parseVehicle
    return {
      ...vehicleData,
      ...parseRefinement(type),
    }
  }, [attributes, parseAttribute, parseVehicle, parseRefinement])

  useEffect(() => {
    const refineParams = window?.location?.search
    const hasParams = refineParams && (refineParams.includes('vehicle') || refineParams.includes('refinements'))
    if (isDataPresent || !products || hasParams) {
      return
    }
    // Determine Type
    let type = 'Part'
    if (
      pageType.includes('part') ||
      pageType.includes('make_part') ||
      pageType.includes('make_model_part')
    ) {
      type = 'Brand'
    }
    // Check if refinement is available
    const { refinements } = products
    const { available } = refinements
    const indexNumber = available.findIndex(record => record.name === type)
    if (indexNumber === -1) {
      setPopularProducts([])
      return
    }
    // Parse URI
    const uri = canonicalUri
      ? canonicalUri.substr(1) : window.location.pathname.substr(1)
    const apiUrl = `${baseUrl}/v1/popular-products`
    const params = {
      domain: localConfig.hostname,
      type,
      page_type: pageType ?? null,
      uri,
      ...otherQuery(type),
    }
    axios.get(apiUrl, {
      params,
      headers: {
        apikey: apiConfig.apiKey,
      },
    }).then(result => {
      if (isMounted()) {
        setPopularProducts(result.data.data?.popularProducts)
      }
    }).catch(err => {
      nrLogger.error('Error fetching Popular Products', err)
    })
  }, [baseUrl, canonicalUri, isDataPresent, isMounted, otherQuery, pageType, products])
  return (
    <PopularProductsContext.Provider value={contextValue}>
      { children }
    </PopularProductsContext.Provider>
  )
}
